import {useEffect} from 'react';
import {Images, MousePointerClick, PanelsTopLeft, Sparkles, Type} from 'lucide-react';
import {
  Button,
  ErrorPlaceholder,
  JobLoader,
  type StepDefinition,
} from '@tinct/web-components';
import {useFunnel} from '@tinct/web-funnel';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {CampaignCreationFunnelState} from '../../campaign-creation-funnel/campaign-creation-funnel';
import {useCampaignAnalysisJob} from './hooks/use-campaign-analysis-job';

const campaignSteps: StepDefinition[] = [
  {title: 'Page structure', icon: <PanelsTopLeft />, duration: 10, strategy: 'linear'},
  {title: 'Content blocks', icon: <Type />, duration: 8, strategy: 'linear'},
  {title: 'Visual elements', icon: <Images />, duration: 12, strategy: 'linear'},
  {title: 'CTAs & forms', icon: <MousePointerClick />, duration: 6, strategy: 'linear'},
  {title: 'Building your template', icon: <Sparkles />, duration: 16, strategy: 'exponentialBackoff'},
];

export const Step2Analyzing = () => {
  const {
    state: {campaign},
  } = useFunnel<CampaignCreationFunnelState>();

  const {navigateToNextStep} = useFunnel();
  const assetsUrl = useStaticAssetsUrl();

  const {subJobs, allCompleted, isAnalysisFailed, startAnalysis, gameModal} =
    useCampaignAnalysisJob(campaign.id || '', campaignSteps);

  useEffect(() => {
    if (allCompleted) {
      navigateToNextStep(true);
    }
  }, [allCompleted, navigateToNextStep]);

  if (isAnalysisFailed) {
    return (
      <ErrorPlaceholder
        image={
          <img
            src={`${assetsUrl}/error-analyze.png`}
            alt=""
            className="h-[140px] w-[280px] object-cover"
          />
        }
        title="Unable to analyze this page"
        description="We couldn't retrieve or process the landing page at this URL. Please check that the URL is correct, the page is publicly accessible, and not behind a login or firewall."
        actions={
          <>
            <Button size="sm" onClick={startAnalysis}>
              Refresh to retry
            </Button>
            <Button size="sm" variant="outline" asChild>
              <a href="mailto:support@tinct.ai">Contact support</a>
            </Button>
          </>
        }
      />
    );
  }

  return (
    <div className="container mx-auto flex w-full flex-col items-center justify-center">
      <div className="flex w-full max-w-xl flex-col gap-8">
        <div className="flex items-end gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <h2 className="font-heading text-3xl font-semibold tracking-tight text-card-foreground">
              Analyzing your landing page...
            </h2>
          </div>
          <div className="flex shrink-0 items-end">
            <img
              src={`${assetsUrl}/run-sampling.png`}
              alt=""
              className="w-[100px]"
            />
          </div>
        </div>

        <JobLoader subJobs={subJobs} />
      </div>
      {gameModal}
    </div>
  );
};
